/**
 * Re-decking a roof.
 *
 * When the old deck is too far gone to patch, the whole thing comes off and new
 * sheathing goes on. That is one line to the customer — "OSB Deck Over" — and
 * underneath it a Home Depot run for the sheets, the nails and the clips, plus
 * the crew's time at a rate per sheet.
 *
 * It also cancels something. Every roofing template carries three sheets of OSB
 * and three 1x6 boards as the patch-repair allowance; a roof getting a whole
 * new deck does not need them as well, and leaving them on would bill the
 * customer for the same material twice.
 *
 * The arithmetic is here rather than in the screen that triggers it because it
 * decides a price, and a price nobody can test is a price nobody can trust.
 */

import {
  addCents,
  cents,
  percentOf,
  toCents,
  toPercent,
  type Cents,
} from "./money.js";
import type { GeneratorProduct, RoofMeasurements } from "./generator.js";
import { quantityFor } from "./generator.js";
import { framingNailFor, type FramingNailer } from "./framingNails.js";

/** A sheet of OSB covers this much of the field, in square feet. */
export const OSB_SHEET_SQFT = 32;

/** Named so the caller can find them in the catalogue without guessing. */
export const DECK_OVER_PRODUCTS = {
  line: "OSB Deck Over",
  sheathing: '7/16" OSB Sheathing 4x8 (Home Depot)',
  nails: "Coil Framing Nails (2500/box)",
  clips: "Panel Sheathing H Clips (250/box)",
  delivery: "Home Depot Delivery Charge",
  labor: "OSB Deck Over Labor",
} as const;

/**
 * The template lines a deck over replaces.
 *
 * Matched on name because that is what the estimate line carries. Both are the
 * patch-repair allowance: a new deck supersedes them.
 */
export const DECK_OVER_SUPERSEDES: readonly string[] = [
  '7/16" OSB',
  '1"X 6"X 16\' Pine Lumber',
];

export interface DeckOverLine {
  product: GeneratorProduct;
  qty: number;
  unitPrice: Cents;
}

export interface DeckOverPlan {
  /** Sheets of OSB, which is also what the labour is paid on. */
  sheets: number;
  materialLines: DeckOverLine[];
  laborLine: DeckOverLine | null;
  /** Material including its tax, plus labour. What the job actually spends. */
  cost: Cents;
  /** Products named above that are missing from the catalogue. */
  missing: string[];
}

const lineCost = (line: DeckOverLine): Cents =>
  cents(line.qty * line.unitPrice);

/**
 * Work out a deck over for a roof.
 *
 * Waste counts on the sheets: the roof is cut up and the offcuts are not
 * re-laid. It does not apply to the nails or the clips, which are bought by
 * coverage of the finished area.
 */
export function planDeckOver(
  measurements: RoofMeasurements,
  products: readonly GeneratorProduct[],
  materialTaxRate: number,
  /**
   * The gun the job's crew nails with, when it is on record. The coil nails
   * are counted as written and then bought in the kind that gun takes (ADR
   * 0287), so the cost — and the price — is the nail the crew will use.
   */
  nailer: FramingNailer | null = null,
): DeckOverPlan {
  const byName = new Map(products.map((p) => [p.name ?? "", p]));
  const missing: string[] = [];

  const find = (name: string): GeneratorProduct | null => {
    const found = byName.get(name);
    if (!found) missing.push(name);
    return found ?? null;
  };

  const sheathing = find(DECK_OVER_PRODUCTS.sheathing);
  const nails = find(DECK_OVER_PRODUCTS.nails);
  const clips = find(DECK_OVER_PRODUCTS.clips);
  const delivery = find(DECK_OVER_PRODUCTS.delivery);
  const labor = find(DECK_OVER_PRODUCTS.labor);

  const squares = Number(measurements.squares ?? 0);
  const waste = Number(measurements.waste_pct ?? 0);
  const materialLines: DeckOverLine[] = [];

  // Sheets come off the field in square feet: a square is a hundred of them.
  const sheets =
    sheathing && squares > 0
      ? (quantityFor(
          squares * 100,
          sheathing.coverage ?? OSB_SHEET_SQFT,
          waste,
        ) ?? 0)
      : 0;

  if (sheathing && sheets > 0) {
    materialLines.push({
      product: sheathing,
      qty: sheets,
      unitPrice: toCents(sheathing.default_cost),
    });
  }

  for (const counted of [nails, clips]) {
    if (!counted || squares <= 0) continue;
    const qty = quantityFor(squares, counted.coverage, 0) ?? 0;
    if (qty <= 0) continue;
    // Counted off the coil box, bought in the crew's: the 30° box carries no
    // coverage of its own, and the number of boxes is the same.
    const product = framingNailFor(counted, nailer, products) ?? counted;
    materialLines.push({
      product,
      qty,
      unitPrice: toCents(product.default_cost),
    });
  }

  // One delivery per run, whatever is on it.
  if (delivery && materialLines.length > 0) {
    materialLines.push({
      product: delivery,
      qty: 1,
      unitPrice: toCents(delivery.default_cost),
    });
  }

  const laborLine =
    labor && sheets > 0
      ? { product: labor, qty: sheets, unitPrice: toCents(labor.default_cost) }
      : null;

  const materialSubtotal = materialLines.reduce(
    (sum, line) => addCents(sum, lineCost(line)),
    toCents(0),
  );
  const materialTotal = addCents(
    materialSubtotal,
    percentOf(materialSubtotal, toPercent(materialTaxRate)),
  );

  return {
    sheets,
    materialLines,
    laborLine,
    cost: addCents(materialTotal, laborLine ? lineCost(laborLine) : toCents(0)),
    missing,
  };
}

/**
 * What to charge for it.
 *
 * The same 40% the roof itself is sold at, solved the same way round: the price
 * that leaves that margin, not the cost with a percentage added. Marking cost
 * up by 40% leaves 28.6%, which is a mistake worth several hundred dollars on a
 * deck over and is easy to make.
 */
export function deckOverPrice(cost: Cents, targetMargin: number): Cents {
  if (
    !Number.isFinite(targetMargin) ||
    targetMargin <= 0 ||
    targetMargin >= 1
  ) {
    return cost;
  }
  return cents(cost / (1 - targetMargin));
}
